import { clearAccessToken, getAccessToken, setAccessToken } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_AUTH_API ?? '/api/v1/auth';

export type AuthUser = {
  id: string;
  email: string;
  email_verified: boolean;
  status: string;
  display_name?: string;
  avatar_url?: string;
  timezone?: string;
  locale?: string;
};

export type TokenPair = {
  access_token: string;
  refresh_token?: string;
  expires_at: string;
  token_type: string;
};

export type Session = {
  id: string;
  user_id: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  last_active: string;
};

export type TrustedDevice = {
  id: string;
  user_id: string;
  device_fingerprint: string;
  device_name: string;
  trusted: boolean;
  last_seen_at: string;
  created_at: string;
};

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extra ?? {}),
  };
}

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: authHeaders(init?.headers as Record<string, string> | undefined),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Auth ${path} failed: ${res.status} ${text}`);
  }
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const authApi = {
  register: (email: string, password: string) =>
    authFetch<{ user: AuthUser; verification_token?: string }>('/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  login: async (email: string, password: string, rememberMe = true) => {
    const data = await authFetch<{ tokens: TokenPair; user: AuthUser }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, remember_me: rememberMe }),
    });
    setAccessToken(data.tokens.access_token);
    return data;
  },
  refresh: async () => {
    const pair = await authFetch<TokenPair>('/refresh', { method: 'POST', body: JSON.stringify({}) });
    setAccessToken(pair.access_token);
    return pair;
  },
  logout: async () => {
    try {
      await authFetch<{ message: string }>('/logout', { method: 'POST' });
    } finally {
      clearAccessToken();
    }
  },
  me: () => authFetch<AuthUser>('/me'),
  updateProfile: (profile: Partial<Pick<AuthUser, 'display_name' | 'avatar_url' | 'timezone' | 'locale'>>) =>
    authFetch<AuthUser>('/me', { method: 'PUT', body: JSON.stringify(profile) }),
  verifyEmail: (token: string) =>
    authFetch<{ message: string }>('/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  requestPasswordReset: (email: string) =>
    authFetch<{ reset_token?: string; message?: string }>('/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (token: string, newPassword: string) =>
    authFetch<{ message: string }>('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    authFetch<{ message: string }>('/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
  listSessions: () => authFetch<Session[]>('/sessions'),
  revokeSession: (id: string) =>
    authFetch<{ message: string }>(`/sessions/${id}`, { method: 'DELETE' }),
  revokeAllSessions: () =>
    authFetch<{ message: string }>('/logout-all', { method: 'POST' }),
  listTrustedDevices: () => authFetch<TrustedDevice[]>('/trusted-devices'),
  removeTrustedDevice: (id: string) =>
    authFetch<{ message: string }>(`/trusted-devices/${id}`, { method: 'DELETE' }),
};
